import type React from 'react';
import { Link } from '@tanstack/react-router';
import { LayoutDashboard, Users, Image, FolderOpen, Wrench, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  testId: string;
}

const navItems: NavItem[] = [
  {
    to: '/admin',
    icon: LayoutDashboard,
    label: 'Dashboard',
    testId: 'admin-nav-dashboard',
  },
  {
    to: '/admin/users',
    icon: Users,
    label: 'Users',
    testId: 'admin-nav-users',
  },
  {
    to: '/admin/photos',
    icon: Image,
    label: 'Photos',
    testId: 'admin-nav-photos',
  },
  {
    to: '/admin/albums',
    icon: FolderOpen,
    label: 'Albums',
    testId: 'admin-nav-albums',
  },
  {
    to: '/admin/maintenance',
    icon: Wrench,
    label: 'Maintenance',
    testId: 'admin-nav-maintenance',
  },
];

/**
 * AdminSidebar - Navigation sidebar for admin panel
 *
 * Features:
 * - Responsive design (mobile menu + desktop sidebar)
 * - Active route highlighting
 * - Icon-based navigation
 * - Glassmorphism styling
 * - Accessibility support
 *
 * Usage:
 * ```tsx
 * <AdminSidebar />
 * ```
 */
export const AdminSidebar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        data-testid="admin-mobile-menu-button"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className={cn(
          'fixed bottom-4 right-4 z-50',
          'md:hidden',
          'flex items-center justify-center',
          'w-12 h-12 rounded-full',
          'bg-primary text-primary-foreground',
          'shadow-lg hover:shadow-xl',
          'transition-all',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
        )}
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          data-testid="admin-mobile-overlay"
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={cn(
          // Mobile: slide in from left
          'fixed left-0 top-[65px] bottom-0 z-40',
          'md:sticky md:top-[65px]',
          'w-64 shrink-0',
          'bg-card/50 backdrop-blur-sm',
          'border-r border-border/50',
          'transition-transform duration-300 ease-in-out',
          'md:translate-x-0',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
          'md:h-[calc(100vh-65px)]'
        )}
      >
        <nav
          data-testid="admin-sidebar-nav"
          role="navigation"
          aria-label="Admin navigation"
          className="p-4 space-y-2"
        >
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              onClick={closeMobileMenu}
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg',
                'text-sm font-medium',
                'transition-all duration-200',
                'hover:bg-primary/10 hover:text-primary',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
              )}
              activeProps={{
                className: cn('bg-primary/10 text-primary', 'border-l-4 border-primary', 'pl-3'),
              }}
            >
              <item.icon className="w-5 h-5" aria-hidden="true" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
};
